import { useCallback } from 'react';
import type { BillingConfigEdit, MonthEdit } from '../services/budgetBillingConfig';
import { PendingCellEdits, PendingEdit, usePendingCellEdits } from './usePendingCellEdits';

/** Which part of a billing config an edit belongs to — `field` is a config column, the rest are
 *  monthly series named after the request payload's own fields, so the builder below is a regrouping
 *  and nothing else. */
export type EditKind = 'field' | 'minutePriceEdits' | 'contractABTEdits' | 'bonusEdits';

export interface PendingConfigEdits extends PendingCellEdits {
    payload: () => BillingConfigEdit[];
}

/** Group a page's pending edits into the billing-config batch payload: one entry per config, carrying
 *  its changed columns and its changed months. Exported for the Overview, which builds one of these for
 *  the configs it edits and its own shapes for the rest. */
export const billingConfigPayload = (edits: PendingEdit[]): BillingConfigEdit[] => {
    const byConfig = new Map<number, BillingConfigEdit>();

    for (const edit of edits) {
        const entry = byConfig.get(edit.rowId) ?? { id: edit.rowId };

        if (edit.kind === 'field') {
            entry.fields = { ...entry.fields, [edit.key]: edit.value };
        } else {
            const month: MonthEdit = { month: edit.key, value: edit.value as number | null };
            const kind = edit.kind as Exclude<EditKind, 'field'>;
            entry[kind] = [...(entry[kind] ?? []), month];
        }

        byConfig.set(edit.rowId, entry);
    }

    return [...byConfig.values()];
};

// The billing-config flavour of the pending store (CALL-779): the generic cell store plus the one
// payload shape the Billing Config and Prices & ABT pages post.
export const usePendingConfigEdits = (): PendingConfigEdits => {
    const pending = usePendingCellEdits();
    const { edits } = pending;

    const payload = useCallback(() => billingConfigPayload(edits), [edits]);

    return { ...pending, payload };
};
